/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TextButton } from '@oneaudi/unified-web-alpha-components';
import useInputsValidator from '../../hooks/useInputsValidator';
import { useElspFormSubmission } from '../../hooks/useFormSubmission';
import { selectDealerAndVehicleData } from '../../redux/dealerAndVehicleReducer';
import { selectForm } from '../../redux/formReducer';
import { Spinner, SubmitCTA } from './Submit.styles';
import { FEATURE_APP_DETAILS } from '../../constants';
import { digitalLeadsFormSubmissionAttempt } from '../../utils/analyticsEventListeners';
import { setError, setLeadId } from '../../redux/localConfigReducer';
import { selectAppConfig } from '../../redux/appConfigReducer';
import { convertToPromotionsType } from '../../functions/convertToPromotionsType';
import { getVehicleForPromotions } from '../../functions/getVehicleForPromotions';

const Submit: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const handleElspSubmit = useElspFormSubmission();
  const isFormValid = useInputsValidator();
  const { vehicle, vehicleByCarline, vehicleByAudiCode } = useSelector(selectDealerAndVehicleData);
  const contactForm = useSelector(selectForm);

  const { offers, vehicleId, carline, audiCode } = useSelector(selectAppConfig);
  const promotion = convertToPromotionsType(offers!);
  const vehicleInView = vehicle || vehicleByCarline || vehicleByAudiCode;
  const requiresVehicleData = !!(vehicleId || carline || audiCode);
  const isSubmitDisabled = !isFormValid() || (requiresVehicleData && !vehicleInView);

  const ctaTextLabel = t('digitalLeads.inputs.submitCtaLabel');
  const submitElement = { ctaText: ctaTextLabel, ctaType: 'submit', urlLink: '' };
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Use vehicle for VTP, vehicleByCarline/vehicleByAudiCode for NGC/MLP, or offers data for promotions
    const vehicleData = vehicleInView || (offers ? getVehicleForPromotions(offers) : undefined);

    digitalLeadsFormSubmissionAttempt(
      contactForm,
      vehicleData,
      FEATURE_APP_DETAILS,
      promotion,
      submitElement,
    );

    setLoading(true);
    const response = await handleElspSubmit();
    setLoading(false);
    const error = response?.error;
    const leadId = response?.leadId || response?.handraiserId;
    if (leadId) return dispatch(setLeadId(leadId));

    let errorMessage = { code: 0, message: '' };
    if (error?.message) {
      if (error?.isConfigError) {
        const [message] = error.message;

        errorMessage.code = 0;
        errorMessage.message = message;
      } else {
        [errorMessage] = error.message;
      }
    }
    return dispatch(setError(errorMessage));
  };

  if (!process.env.IS_LEGACY) {
    return (
      <TextButton
        onClick={onSubmit}
        isFullWidth
        variant="primary"
        disabled={isSubmitDisabled}
        data-testid="submit-cta"
      >
        {loading ? <Spinner /> : ctaTextLabel}
      </TextButton>
    );
  }

  return (
    <SubmitCTA onClick={onSubmit} disabled={isSubmitDisabled} data-testid="submit-cta">
      {loading ? <Spinner /> : ctaTextLabel}
    </SubmitCTA>
  );
};

export default Submit;
